/* global React, Icon, Card, Button, Modal, Select, toast */
// ============================================================
// PLUMME 3 — Privat økonomi · Import
// Opplasting av kontoutskrifter, kontokobling og importstatus.
// All lagring går via backend (uploadPersonalFinanceBankStatement,
// linkPersonalFinanceImportJobAccount, deletePersonalFinanceImportJob).
// ============================================================
const { useState: poiUseState, useRef: poiUseRef } = React;

const poiStatus = (status) => {
  if (status === 'processed') return [t('pkn.acc.readIn'), 'pp-paid'];
  if (status === 'failed') return [t('pkn.acc.failed'), 'pp-warn'];
  if (status === 'processing') return [t('pkn.imp.beingRead'), 'pp-warn'];
  return [t('pkn.acc.uploaded'), 'pp-warn'];
};
const poiFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

// Kontovalg for en opplastet eller nettopp valgt kontoutskrift. Brukes både når
// en ny fil lastes opp og når en eksisterende utskrift skal kobles i etterkant.
function PoImportAccountModal({ accounts = [], busy = false, fileName = '', onClose, onSave, submitLabel }) {
  const initial = accounts[0] || null;
  const suggestedName = String(fileName || '').replace(/\.[^.]+$/, '') || 'Importert konto';
  const [accountId, setAccountId] = poiUseState(initial?.id || '');
  const [draft, setDraft] = poiUseState(() => ({
    accountNumber: initial?.accountNumber || '',
    closingBalance: initial?.closingBalance ?? initial?.currentBalance ?? '',
    kind: initial?.kind || 'bank',
    name: initial?.name || suggestedName,
    openingBalance: initial?.openingBalance ?? '',
  }));
  const selected = accounts.find((account) => account.id === accountId) || null;
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const selectAccount = (value) => {
    setAccountId(value);
    const account = accounts.find((entry) => entry.id === value);
    setDraft(account
      ? { accountNumber: account.accountNumber || '', closingBalance: account.closingBalance ?? account.currentBalance ?? '', kind: account.kind || 'bank', name: account.name || '', openingBalance: account.openingBalance ?? '' }
      : { accountNumber: '', closingBalance: '', kind: 'bank', name: suggestedName, openingBalance: '' });
  };
  const save = () => {
    if (busy) return;
    if (!selected && !String(draft.name || '').trim()) {
      toast(t('pkn.acc.fillName'), 'error');
      return;
    }
    onSave?.(selected
      ? { ...selected, accountNumber: draft.accountNumber || selected.accountNumber, closingBalance: draft.closingBalance, currentBalance: draft.closingBalance, openingBalance: draft.openingBalance }
      : { ...draft, name: String(draft.name || '').trim() });
  };
  return <Modal open onClose={onClose} title={t('po.linkStatementToAccount')} max={560}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button icon="check" disabled={busy} onClick={save}>{submitLabel || t('po.linkToAccount')}</Button></React.Fragment>}>
    <div className="gj-form">
      {fileName && <div className="po-upload-row"><Icon name="fileText" size={18} /><div><b>{fileName}</b><span>{t('pkn.imp.pickAccountFor')}</span></div></div>}
      {accounts.length > 0 && <label className="gj-field" data-help={t('helpTopics.accountChoice.body')} data-help-title={t('helpTopics.accountChoice.title')}><span  >{t('voucher.account')}</span>
        <Select value={accountId} onChange={(event) => selectAccount(event.target.value)}>
          {accounts.map((account) => <option key={account.id} value={account.id}>{account.name || t('pkn.acc.account')}{account.accountNumber ? ` · ${account.accountNumber}` : ''}</option>)}
          <option value="">{t('po.newAccount')}</option>
        </Select>
      </label>}
      <label className="gj-field"><span>{t('clo.accountName')}</span><input className="gj-input" value={draft.name || ''} disabled={Boolean(selected)} placeholder={t('po.accountNamePh')} onChange={(event) => set('name', event.target.value)} /></label>
      <label className="gj-field"><span>{t('set.bank.accountNumber')}</span><input className="gj-input" value={draft.accountNumber || ''} placeholder={t('po.accountNumberPh')} onChange={(event) => set('accountNumber', event.target.value)} /></label>
      <label className="gj-field"><span>{t('sale.pay.accountType')}</span>
        <Select value={draft.kind || 'bank'} disabled={Boolean(selected)} onChange={(event) => set('kind', event.target.value)}>
          <option value="bank">{t('po.currentAccount')}</option>
          <option value="savings">{t('pay.savingsAccount')}</option>
          <option value="credit-card">{t('po.creditCard')}</option>
        </Select>
      </label>
      <div className="gj-form-row">
        <label className="gj-field" data-help={t('helpTopics.opening.body')} data-help-title={t('helpTopics.opening.title')}><span  >{t('bank.openingBalance')}</span><Input className="gj-input" inputMode="decimal" value={draft.openingBalance ?? ''} placeholder={t('pay.optional')} onChange={(event) => set('openingBalance', window.PlummeMoneyInput.normalizeEditable(event.target.value,{previousValue:draft.openingBalance}))} /></label>
        <label className="gj-field" data-help={t('helpTopics.closingBalance.body')} data-help-title={t('helpTopics.closingBalance.title')}><span  >{t('asset.closing')}</span><Input className="gj-input" inputMode="decimal" value={draft.closingBalance ?? ''} placeholder={t('pay.optional')} onChange={(event) => set('closingBalance', window.PlummeMoneyInput.normalizeEditable(event.target.value,{previousValue:draft.closingBalance}))} /></label>
      </div>
      <span className="gj-hint">{t('pkn.imp.numberHint')}</span>
    </div>
  </Modal>;
}

function PoImport({
  accounts = [], error = '', jobs = [], loading = false, onAsk, onDeleteAccount, onDeleteJob, onLinkJob, onRecategorize, onSaveAccount, onUpload,
  recategorizing = false, summary = {}, transactions = [], uploading = false,
  year,
}) {
  const Sec = window.PoSecHead;
  const Says = window.PlummaSays;
  const fileInputRef = poiUseRef(null);
  // Filen ligger i vent til brukeren har valgt konto i dialogen.
  const [pendingFile, setPendingFile] = poiUseState(null);
  const [linkJob, setLinkJob] = poiUseState(null);
  const [confirmJob, setConfirmJob] = poiUseState(null);
  const [deletingJobId, setDeletingJobId] = poiUseState('');

  // Utskrifter uten konto vises her; resten ligger inne på kontoen de tilhører.
  const unlinkedJobs = jobs.filter((job) => !job.accountId);

  const pickFile = (files) => {
    const file = Array.from(files || [])[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || uploading) return;
    setPendingFile(file);
  };

  const upload = (account) => {
    const file = pendingFile;
    setPendingFile(null);
    if (!file) return;
    onUpload?.(file, {
      accountNumber: account.accountNumber,
      closingBalance: account.closingBalance,
      id: account.id || undefined,
      kind: account.kind || 'bank',
      name: account.name || String(file.name || '').replace(/\.[^.]+$/, '') || 'Importert konto',
      openingBalance: account.openingBalance,
    });
  };

  const deleteJob = async (job) => {
    if (!job?.id || deletingJobId) return;
    setDeletingJobId(job.id);
    try { await onDeleteJob?.(job); } finally { setDeletingJobId(''); }
  };

  const stats = [
    ['list', t('pkn.acc.kpi.read'), summary.transactions || transactions.length || 0],
    ['pie', t('pkn.acc.kpi.categories'), summary.categories || 0],
    ['bank', t('pkn.imp.accounts'), accounts.length],
    ['fileText', t('pkn.imp.statements'), jobs.length],
  ];

  return <React.Fragment>
    {Says && <Says action={onAsk ? t('pkn.fam.askSuperPlumma') : null} onAction={onAsk}>
      {t('pkn.imp.sayA')} <b>{t('po.me')}</b> {t('pkn.imp.sayB')}
    </Says>}

    {window.PoKontoer && <window.PoKontoer
      accounts={accounts}
      jobs={jobs}
      transactions={transactions}
      onSaveAccount={onSaveAccount}
      onDeleteAccount={onDeleteAccount}
      onDeleteJob={onDeleteJob}
      year={year}
    />}

    <Card>
      <Sec icon="upload" title={t('po.importTitle')} sub={t('po.importSub')}
        right={onRecategorize && transactions.length > 0
          ? <Button size="sm" variant="secondary" icon="sparkle" disabled={recategorizing} onClick={onRecategorize}>
            {recategorizing ? t('pkn.imp.recategorising') : t('pkn.imp.recategorise')}
          </Button>
          : null} />

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="po-file-input"
        aria-label={t('po.pickStatement')}
        onChange={(event) => pickFile(event.target.files)}
      />

      <div data-app-control="true"
        className={`po-drop${uploading ? ' is-uploading' : ''}`}
        data-help-title={t('po.importTitle')}
        data-help={t('po.pickStatementHelp')}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { event.preventDefault(); pickFile(event.dataTransfer.files); }}
      >
        <span className="po-drop-ico"><Icon name="upload" size={26} /></span>
        <div className="po-drop-title">{uploading ? t('pkn.imp.uploadingDots') : t('pkn.imp.dropHere')}</div>
        <div className="po-drop-sub">{t('pkn.imp.dropSub')}</div>
        <div className="po-drop-formats"><span className="po-fmt">{t('po.pdf')}</span><span className="po-fmt">{t('set.csv')}</span><span className="po-fmt">{t('set.xlsx')}</span></div>
        <div className="row gap-10" style={{ marginTop: 8 }}>
          <Button size="sm" icon="upload" disabled={uploading} onClick={(event) => { event.stopPropagation(); fileInputRef.current?.click(); }}>{uploading ? t('pkn.imp.uploading') : t('pkn.imp.chooseFiles')}</Button>
        </div>
      </div>

      {error && <div className="po-import-error">{error}</div>}

      {loading && <div className="po-upload-empty" style={{ marginTop: 16 }}>{t('pkn.imp.fetching')}</div>}

      {unlinkedJobs.length > 0 && <div className="po-upload-list">
        <div className="po-upload-empty" style={{ margin: 0, textAlign: 'left' }}>{t('pkn.imp.unlinkedNote')}</div>
        {unlinkedJobs.map((job) => {
          const [label, badgeClass] = poiStatus(job.status);
          const file = job.file || {};
          const meta = [String(file.mimeType || '').split('/').pop()?.toUpperCase(), poiFileSize(file.size)].filter(Boolean).join(' · ');
          return <div className="po-upload-item" key={job.id || file.storagePath || file.name}>
            <span className="po-upload-ico"><Icon name="fileText" size={18} /></span>
            <div className="po-upload-meta">
              <div className="po-upload-name">{file.name || t('pkn.acc.statement')}</div>
              <div className="po-upload-sub">{job.errorMessage || meta || t('pkn.acc.uploaded')}</div>
              {job.status === 'processing' && <div className="po-upload-bar"><span style={{ width: '64%' }} /></div>}
            </div>
            <span className={`pp ${badgeClass}`}>{label}</span>
            <button className="po-upload-link" type="button" onClick={() => setLinkJob(job)} data-help={t('helpTopics.privateImport.body')} data-help-title={t('helpTopics.privateImport.title')}>{t('po.linkToAccount')}</button>
            <button className="po-upload-delete" disabled={deletingJobId === job.id} onClick={() => setConfirmJob(job)} title={t('po.deleteStatement')} aria-label={t('po.deleteStatement')} data-help={t('helpTopics.privateImport.body')} data-help-title={t('helpTopics.privateImport.title')}>
              <Icon name="trash" size={16} />
            </button>
          </div>;
        })}
      </div>}

      <div className="po-proc-grid">
        {stats.map(([icon, label, value]) => <div key={label} className="po-proc">
          <div className="po-proc-top"><Icon name={icon} size={15} />{label}</div>
          <div className="po-proc-val">{value}</div>
        </div>)}
      </div>
    </Card>

    {pendingFile && <PoImportAccountModal
      accounts={accounts}
      busy={uploading}
      fileName={pendingFile.name}
      submitLabel={t('pkn.imp.upload')}
      onClose={() => setPendingFile(null)}
      onSave={upload}
    />}

    {linkJob && <PoImportAccountModal
      accounts={accounts}
      fileName={linkJob.file?.name || ''}
      onClose={() => setLinkJob(null)}
      onSave={(account) => { onLinkJob?.(linkJob, account); setLinkJob(null); }}
    />}

    {confirmJob && <ConfirmModal
      title={t('po.deleteStatement')}
      confirmLabel={t('list.delete')}
      busy={deletingJobId === confirmJob.id}
      onClose={() => setConfirmJob(null)}
      onConfirm={() => { const job = confirmJob; setConfirmJob(null); deleteJob(job); }}
    >
      {t('pkn.acc.deleting')} <b>{confirmJob.file?.name || t('pkn.acc.theStatement')}</b> {t('pkn.acc.deletingSuffix')}
    </ConfirmModal>}
  </React.Fragment>;
}

Object.assign(window, { PoImport });
